import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,Users,Shield, ShoppingCart, Ticket, FileText, Settings, Key, Activity, LogOut,
  Monitor, Package, Tag, Mail, ClipboardList, Lock, UserCircle, Briefcase, HeartPulse, Database,
  AlertTriangle, Rocket, BrainCircuit, Zap, Crosshair, Globe, Bot,
  Globe2Icon
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../hooks/usePermission';

/* ------------------ NAV ITEM ------------------ */
const NavItem = ({ to, icon: Icon, label, badge }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `relative flex items-center px-4 py-3.5 text-sm font-medium rounded-xl transition-all duration-300 group overflow-hidden mb-1 ${
        isActive
          ? 'bg-gradient-to-r from-airs-600 to-airs-700 text-white shadow-lg shadow-airs-500/25'
          : 'text-slate-400 hover:text-slate-100 hover:bg-white/5'
      }`
    }
  >
    {({ isActive }) => (
      <>
        <Icon className={`w-5 h-5 mr-3 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
        <span>{label}</span>
        {badge && (
          <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-airs-500/20 text-airs-300">
            {badge}
          </span>
        )}
      </>
    )}
  </NavLink>
);

/* ------------------ SECTION ------------------ */
const SidebarSection = ({ title }) => (
  <p className="px-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-6 mb-2">
    {title}
  </p>
);

/* ------------------ SIDEBAR ------------------ */
const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { logout } = useAuth();
  const { can, role } = usePermission();

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/80 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      <aside className={`fixed lg:relative top-0 left-0 z-50 h-full w-72 bg-[#0b0f19] text-white flex flex-col transition-transform ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>

        {/* HEADER */}
        <div className="h-20 flex items-center px-6 border-b border-slate-800">
          <Shield className="w-6 h-6 text-airs-500 mr-3" />
          <div>
            <h1 className="text-lg font-bold">
              AIRS<span className="text-airs-500">Security</span>
            </h1>
            <p className="text-[10px] uppercase tracking-wider text-slate-500">
              {role === 'super_admin' ? 'Command Center' : 'Staff Portal'}
            </p>
          </div>
        </div>

        {/* MENU */}
        <div className="flex-1 overflow-y-auto px-4 py-4 custom-scrollbar">

          <SidebarSection title="Overview" />
          <NavItem to="/" icon={LayoutDashboard} label="Dashboard" />
          {can('dashboard:view') && (
            <NavItem to="/stats" icon={Activity} label="Live Activity" badge="LIVE" />
          )}

          <SidebarSection title="Advanced Detection" />
          <NavItem to="/alerts/threat-detections" icon={AlertTriangle} label="Threats Alerts" badge="AI" />

          {can('logs:view') && (
            <NavItem to="/soc-ledger" icon={Zap} label="Autonomous SOC" badge="AGI" />
          )}

          {role === 'super_admin' && (
            <NavItem to="/intruder-map" icon={Crosshair} label="Intruder Trace Map" badge="LIVE" />
          )}

          {role === 'super_admin' && (
            <NavItem to="/cognitive-pulse" icon={BrainCircuit} label="Cognitive Pulse" badge="BRAIN" />
          )}
          
          {role === 'super_admin' && (
          <NavItem to="/forensic-ledger" icon={FileText} label="Forensic Reports" badge="PDF" />
          )}

          {role === 'super_admin' && (
          <NavItem to="/web-auditor" icon={Globe2Icon} label="Web Auditor" badge="OCULUS" />
          )}
          
          {(can('devices:view') || can('licenses:view')) && (
            <SidebarSection title="Defense Grid" />
          )}

          {role === 'super_admin' && (
            <NavItem to="/war-map" icon={Globe} label="Strategic War Map" badge="OMEGA" />
          )}
          
          {role === 'super_admin' && (
            <NavItem to="/admin/swarm" icon={Bot} label="Autonomous Swarm" badge="AGI" />
          )}

          {can('devices:view') && <NavItem to="/devices" icon={Monitor} label="Device Fleet" />}
          
          {can('devices:view') && <NavItem to="/behavior" icon={BrainCircuit} label="Behavior Analytics" badge="AI" />}
          
          {can('licenses:view') && <NavItem to="/licenses" icon={Key} label="License Keys" />}

          {(can('customers:view') || can('orders:view') || can('packages:view')) && (
            <SidebarSection title="Operations" />
          )}
          {can('customers:view') && <NavItem to="/customers" icon={Users} label="Customers" />}
          {can('orders:view') && <NavItem to="/orders" icon={ShoppingCart} label="Orders & Billing" />}
          {can('packages:view') && <NavItem to="/packages" icon={Package} label="Plans & Packages" />}
          {can('coupons:view') && <NavItem to="/coupons" icon={Tag} label="Coupons & Promo" />}

          {(can('staff:manage') || can('roles:manage')) && (
            <SidebarSection title="Team & Access" />
          )}
          {can('staff:manage') && <NavItem to="/staff" icon={Shield} label="Staff Management" />}
          {can('roles:manage') && <NavItem to="/roles" icon={Briefcase} label="Roles & Permissions" />}

          <SidebarSection title="Support & System" />
          {can('tickets:view') && <NavItem to="/tickets" icon={Ticket} label="Support Desk" />}
          {can('contact:view') && <NavItem to="/contact-messages" icon={Mail} label="Contact Inbox" />}
          {can('cms:view') && <NavItem to="/cms" icon={FileText} label="CMS Content" />}
          {can('logs:view') && <NavItem to="/audit-logs" icon={ClipboardList} label="Audit Logs" />}
          {can('logs:view') && <NavItem to="/system-health" icon={HeartPulse} label="System Health" />}
          {can('logs:view') && <NavItem to="/database" icon={Database} label="Database & Backup" />}
          {can('logs:view') && <NavItem to="/agent-debug" icon={AlertTriangle} label="Agent Blackbox" />}
          
          {role === 'super_admin' && (
            <NavItem to="/system/releases" icon={Rocket} label="Release Manager" />
          )}

          <SidebarSection title="My Account" />
          <NavItem to="/my-profile" icon={UserCircle} label="Profile & Security" />

          {can('settings:view') && <NavItem to="/settings" icon={Settings} label="Global Settings" />}
          {can('settings:view') && <NavItem to="/access-control" icon={Lock} label="Security & Access" />}
        </div>

        {/* FOOTER */}
        <div className="p-4 border-t border-slate-800">
          <button
            onClick={logout}
            className="w-full flex items-center justify-center py-3 text-red-400 hover:text-red-300"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </button>
        </div>

      </aside>
    </>
  );
};

export default Sidebar;
